import envConfig from '@hulu/env-config';
import { getJwtTokenPayload } from 'mission-control-common-components';
import type { CognitoDetails } from 'mission-control-common-components/src/util/user/types';

import { getFlag } from './launchDarkly/ldClient';

export const getCognitoDetails = (newCognitoPoolEnabled: boolean): CognitoDetails => {
  if (newCognitoPoolEnabled) {
    return { host: envConfig.REACT_APP_COGNITO_HOST_NEW, clientId: envConfig.REACT_APP_COGNITO_CLIENT_ID_NEW };
  }

  return {
    host: envConfig.REACT_APP_COGNITO_HOST,
    clientId: envConfig.REACT_APP_COGNITO_CLIENT_ID,
  };
};

const normalizeAudiences = (aud?: string | string[]): string[] => {
  if (!aud) {
    return [];
  }

  return Array.isArray(aud) ? aud : [aud];
};

/** Reads which configured pool issued the current token from its `aud` claim. */
export const getNewCognitoPoolEnabledFromToken = (idToken: string | null): boolean | undefined => {
  if (!idToken) {
    return undefined;
  }

  const tokenPayload = getJwtTokenPayload(idToken) as { aud?: string | string[] } | null;
  const audiences = normalizeAudiences(tokenPayload?.aud);
  const [newClientId, oldClientId] = [envConfig.REACT_APP_COGNITO_CLIENT_ID_NEW, envConfig.REACT_APP_COGNITO_CLIENT_ID];

  if (newClientId && audiences.includes(newClientId)) {
    return true;
  }

  if (oldClientId && audiences.includes(oldClientId)) {
    return false;
  }

  return undefined;
};

/**
 * Pool for token refresh / GraphQL. Must match the token issuer even when LaunchDarkly evaluates
 * false on a stale client — otherwise checkIdToken clears the session and login uses the wrong pool.
 */
export const getCognitoDetailsForSession = async (): Promise<CognitoDetails> => {
  const idToken = window.localStorage.id_token ?? null;
  const poolFromToken = getNewCognitoPoolEnabledFromToken(idToken);

  if (poolFromToken !== undefined) {
    return getCognitoDetails(poolFromToken);
  }

  const newCognitoPoolEnabled = await getFlag('newCognitoPoolEnabled', undefined);

  if (newCognitoPoolEnabled !== undefined) {
    return getCognitoDetails(newCognitoPoolEnabled);
  }

  return getCognitoDetails(true);
};

/** Pool for initiating login. LaunchDarkly flag leads; token aud is fallback only. */
export const getCognitoDetailsForLogin = async (): Promise<CognitoDetails> => {
  const newCognitoPoolEnabled = await getFlag('newCognitoPoolEnabled', undefined);

  if (newCognitoPoolEnabled !== undefined) {
    return getCognitoDetails(newCognitoPoolEnabled);
  }

  const poolFromToken = getNewCognitoPoolEnabledFromToken(window.localStorage.id_token ?? null) ?? true;
  return getCognitoDetails(poolFromToken);
};

/** Sync resolver for Header / logout controls while React LD flags are still loading. */
export const getCognitoDetailsFromFlagsOrToken = (
  newCognitoPoolEnabled: boolean | undefined | null
): CognitoDetails => {
  const poolFromToken = getNewCognitoPoolEnabledFromToken(window.localStorage.id_token ?? null);

  if (poolFromToken !== undefined) {
    return getCognitoDetails(poolFromToken);
  }

  if (newCognitoPoolEnabled !== undefined && newCognitoPoolEnabled !== null) {
    return getCognitoDetails(newCognitoPoolEnabled);
  }

  return getCognitoDetails(true);
};

/** @deprecated Use getCognitoDetailsForSession or getCognitoDetailsForLogin. */
export const getCognitoDetailsCached = getCognitoDetailsForSession;
